import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDragStore } from '../../stores/dragStore';

const SNAP_DISTANCE = 30; // Distance in pixels to trigger snap

export function DragPortal() {
  const {
    isDragging,
    draggedColor,
    currentX,
    currentY,
    offsetX,
    offsetY,
    draggedSquareIndex,
  } = useDragStore();

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (e: MouseEvent): void => {
      const state = useDragStore.getState();
      const newX = e.clientX - state.offsetX;
      const newY = e.clientY - state.offsetY;

      state.updateDragPosition(newX, newY);
    };

    const handleMouseUp = (e: MouseEvent): void => {
      const state = useDragStore.getState();

      // Check for snap to Oncall squares on mouse up
      if (state.draggedSquareIndex !== null) {
        for (let i = 0; i < 4; i += 1) {
          const squarePos = state.getOncallSquarePosition(i);
          if (!squarePos) {
            // eslint-disable-next-line no-continue
            continue;
          }

          const squareCenterX = squarePos.x + squarePos.width / 2;
          const squareCenterY = squarePos.y + squarePos.height / 2;
          const draggedCenterX = e.clientX;
          const draggedCenterY = e.clientY;

          const distance = Math.sqrt(
            (draggedCenterX - squareCenterX) ** 2 +
              (draggedCenterY - squareCenterY) ** 2,
          );

          if (distance < SNAP_DISTANCE) {
            state.snapToOncall(i);
            return;
          }
        }
      }

      // If not snapped, reset drag
      state.resetDrag();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, draggedSquareIndex, offsetX, offsetY]);

  if (!isDragging || !draggedColor) return null;

  return createPortal(
    <div
      className={`fixed w-5 h-5 ${draggedColor} pointer-events-none shadow-lg`}
      style={{
        left: `${currentX}px`,
        top: `${currentY}px`,
        zIndex: 10000,
      }}
    />,
    document.body,
  );
}
